import { z } from 'zod';
import { RefinementCtx } from 'zod/lib/types';

import { OptionItemObject } from '../../../shared_models/interfaces';
import { HEADER_1_NAME, TrueFalseArray, VALID_HEADERS_NAMES } from '../model';

import { convertToIssues } from './ImportExportJson.zod';
import { getFirstDuplicateFromList, getSecondUniqueFromList, isStringGuard } from './utils';
import { DUPLICATE_OPTION_NAME_ERROR, INVALID_OPTION_ID_ERROR, INVALID_OPTION_NAME_ERROR, VResult } from './Validator';

const checkRequiredHeaders = (headers: string[]) => {
    for (let index = 0; index < 7; index++) {
        if (headers.length >= index + 1) {
            if (index !== VALID_HEADERS_NAMES.indexOf(headers[index])) {
                return false;
            }
        }
    }
    return true;
};

const validateAttributeGeneric = (
    options: CsvJsonOption[],
    attributeName: keyof CsvJsonOption,
    validation: VResult[] = []
): VResult[] => {
    const levelNames = options.map(option => option[attributeName] as string).filter(isStringGuard);
    const secondUnique = getSecondUniqueFromList(levelNames);
    if (secondUnique) {
        //   return [...parents, secondUnique];
        const option = options.find(item => item[attributeName] === secondUnique)!;
        validation.push({ id: option.id, path: [`row number ${option.rowNumber}`], invalidValue: secondUnique });
    }

    for (const entry of options) {
        if (entry.options.length) {
            const returnValue = validateAttributeGeneric(entry.options, attributeName, validation);
            if (returnValue?.length) {
                return returnValue;
            }
        }
    }

    return validation;
};

const validateAttributeUniques = (attrName: keyof CsvJsonOption) => (options: CsvJsonOption[], ctx: RefinementCtx) => {
    const results = validateAttributeGeneric(options, attrName);
    if (results.length) {
        for (const error of results) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Different ${attrName} for same level`,
                params: [{ invalidValue: error.invalidValue }],
                path: error.path,
            });
        }
    }
};

const atLeastOneOptionOnLevelOne = (options: CsvJsonOption[]) => {
    return options.length > 0;
};

const getOptionById = (options: OptionItemObject[], id: string): OptionItemObject | undefined => {
    return options.find(item => item.key === id);
};

const validateOptionIds = (model: OptionItemObject[]) => (options: CsvJsonOption[], ctx: RefinementCtx) => {
    const results = doValidateOptionIds(model, options, [], []);
    convertToIssues(results, ctx, INVALID_OPTION_ID_ERROR);
};

const getCorrespondingLevel = (option: CsvJsonOption, configOptions: OptionItemObject[]): OptionItemObject[] => {
    const items = configOptions.find(item => item.key === option.id)?.children;
    return items ? items : [];
};

const doValidateOptionIds = (
    configOptions: OptionItemObject[],
    options: CsvJsonOption[],
    validationResult: VResult[] = [],
    parentIndexs: number[]
): VResult[] => {
    options.forEach(option => {
        let index = 0;
        if (option.id) {
            const foundOption = getOptionById(configOptions, option.id);
            if (!foundOption) {
                validationResult.push({
                    invalidValue: option.id,
                    path: [`row number: ${option.rowNumber}`, 'column name: option ID'],
                });
            }
            if (option.options.length) {
                doValidateOptionIds(getCorrespondingLevel(option, configOptions), option.options, validationResult, [
                    ...parentIndexs,
                    index,
                ]);
            }
        }
        index++;
    });
    return validationResult;
};

const findItems = (model: OptionItemObject[], id: string | undefined) => {
    const found = model.find(item => item.key === id);
    return found ? found.children : [];
};
const doValidateUniquenessOfOptionName = (
    model: OptionItemObject[],
    options: CsvJsonOption[],
    validationResult: VResult[] = []
) => {
    const allOptionsNames = [
        ...options.filter(option => option.id === '').map(option => option.optionName),
        ...model.map(option => option.value),
    ];

    const duplicate = getFirstDuplicateFromList(allOptionsNames);
    if (duplicate) {
        validationResult.push({
            path: [`row number: ${options.find(option => option.optionName === duplicate)!.rowNumber}`],
            invalidValue: duplicate,
        });
    }
    for (const entry of options) {
        if (entry.options.length) {
            doValidateUniquenessOfOptionName(findItems(model, entry.id), entry.options, validationResult);
        }
    }
    return validationResult;
};

const doValidateNotEmptyOptionName = (options: CsvJsonOption[], index = { value: 1 }, validationResult: VResult[] = []) => {
    for (const entry of options) {
        if (!entry.optionName) {
            validationResult.push({
                path: [`row number: ${index.value}`],
            });
        }
        index.value++;
        if (entry.options.length) {
            doValidateNotEmptyOptionName(entry.options, index, validationResult);
        }
    }
    return validationResult;
};

const validateUniquenessOfOptionName = (model: OptionItemObject[]) => (options: CsvJsonOption[], ctx: RefinementCtx) => {
    const results = doValidateUniquenessOfOptionName(model, options);
    convertToIssues(results, ctx, DUPLICATE_OPTION_NAME_ERROR);
};

const validateNotEmptyOptionName = (options: CsvJsonOption[], ctx: RefinementCtx) => {
    const results = doValidateNotEmptyOptionName(options);
    convertToIssues(results, ctx, INVALID_OPTION_NAME_ERROR);
};

const validateTrueFalseColumn = (attrName: keyof CsvJsonOption) => (options: CsvJsonOption[], ctx: RefinementCtx) => {
    for (const option of options) {
        // @ts-ignore
        if (option.isEnabled == undefined || !TrueFalseArray.includes(option[attrName])) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Invalid value in column ' + attrName,
                path: ['row number: ' + option.rowNumber],
                params: [{ invalidValue: option[attrName] }],
            });
        }

        if (option.options.length) {
            validateTrueFalseColumn(attrName)(option.options, ctx);
        }
    }
};

const checkOrderHeaders = (headers: string[]) => headers.includes(HEADER_1_NAME);

const predicateIfHeaderNameValid = (headerName: string) => (headerName ? !VALID_HEADERS_NAMES.includes(headerName) : false);

const checkHeaderNames = (headers: string[]) => {
    const invalidHeader = headers.find(predicateIfHeaderNameValid);
    return invalidHeader === undefined;
};

const getInvalidHeaderPosition = (headers: string[]) => {
    const invalidHeader = headers.find(predicateIfHeaderNameValid);
    return invalidHeader ? ` Header column: ${headers.indexOf(invalidHeader)}` : 0;
};

const checkHeadersNamesMessage = (val: string[]) => ({
    message: 'Invalid headers names',
    path: [getInvalidHeaderPosition(val)],
});

const checkEmptyHeadersMessage = {
    message: 'Empty header name is not allowed',
};

const checkOrderHeadersMessage = {
    message: `No required header name ${HEADER_1_NAME}`,
};

const atLeastOneOptionOnLevelOneMessage = {
    message: 'Csv file should have at least 2 lines (headers + one option on level one)',
};

const checkRequiredHeadersMessage = {
    message: 'Invalid order in first 7 columns names',
};

const checkEmptyHeaders = (headers: string[]) => {
    return !headers.some(item => item === '');
};

const CsvJsonOptionBase = z.strictObject({
    isDefault: z.string().optional(),
    isEnabled: z.string().optional(),
    optionName: z.string().optional(),
    id: z.string().optional(),
    levelDescription: z.string().optional(),
    levelName: z.string().optional(),
    rowNumber: z.number(),
});

type CsvJsonOption = z.infer<typeof CsvJsonOptionBase> & {
    options: CsvJsonOption[];
};

const CsvJsonOptionSchema: z.ZodType<CsvJsonOption> = CsvJsonOptionBase.extend({
    options: z.lazy(() => CsvJsonOptionSchema.array()),
});

export const getParsedCsvZod = (model: OptionItemObject[]) =>
    z.object({
        headers: z
            .array(z.string())
            .refine(checkRequiredHeaders, checkRequiredHeadersMessage)
            .refine(checkEmptyHeaders, checkEmptyHeadersMessage)
            .refine(checkHeaderNames, checkHeadersNamesMessage)
            .refine(checkOrderHeaders, checkOrderHeadersMessage),
        rows: z.array(
            z.object({
                'Level 1': z.string().optional(),
                Default: z.string().optional(),
                Enabled: z.string().optional(),
                'ID option': z.string().optional(),
                'Level 2': z.string().optional(),
                'Level 3': z.string().optional(),
                'Level 4': z.string().optional(),
                'Level 5': z.string().optional(),
                'Level 6': z.string().optional(),
                'Level 7': z.string().optional(),
                'Level description': z.string().optional(),
                'Level name': z.string().optional(),
            })
        ),
        options: z
            .array(CsvJsonOptionSchema)
            .superRefine(validateUniquenessOfOptionName(model))
            .superRefine(validateNotEmptyOptionName)
            .superRefine(validateAttributeUniques('levelName'))
            .superRefine(validateAttributeUniques('levelDescription'))
            .refine(atLeastOneOptionOnLevelOne, atLeastOneOptionOnLevelOneMessage)
            .superRefine(validateTrueFalseColumn('isEnabled'))
            .superRefine(validateTrueFalseColumn('isDefault'))
            .superRefine(validateOptionIds(model)),
    });

// @ts-ignore
const ParsedCsvZod = getParsedCsvZod({ treeData: [] });
export type ParsedCsvZodType = z.infer<typeof ParsedCsvZod>;
